import { Capacitor } from "@capacitor/core"
import { type LocalNotificationSchema, LocalNotifications } from "@capacitor/local-notifications"
import { formatMoney } from "../composables/useCurrency"
import { i18n } from "../i18n"
import type { FirestoreCategory } from "../types/firestore"
import { toLocalDateString } from "../utils/localDate"
import { logger } from "../utils/logger"
import { getUpcomingRecurringExpenses } from "./recurringTransactionService"

// Notifications render outside any component, so they translate through the
// global i18n instance (same pattern as seeded categories / offline toasts).
function nt(key: string, params?: Record<string, unknown>): string {
  return i18n.global.t(`notifications.push.${key}`, params ?? {})
}

// Notification channel IDs
export const NOTIFICATION_CHANNELS = {
  BILL_REMINDERS: "bill-reminders",
  BUDGET_ALERTS: "budget-alerts",
  GOAL_MILESTONES: "savings-goals",
} as const

// Notification ID prefixes to avoid collisions
const NOTIFICATION_ID_PREFIX = {
  BILL: 1000,
  BUDGET: 2000,
  GOAL: 3000,
} as const

export interface NotificationPreferences {
  enabled: boolean
  billReminders: boolean
  billReminderDays: number // Days before bill is due
  budgetAlerts: boolean
  budgetAlertThreshold: number // Percentage (e.g., 80 means alert at 80% spent)
  goalMilestones: boolean
  quietHoursEnabled: boolean
  quietHoursStart: number // Hour of day (0-23)
  quietHoursEnd: number // Hour of day (0-23)
}

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  enabled: true,
  billReminders: true,
  billReminderDays: 3,
  budgetAlerts: true,
  budgetAlertThreshold: 80,
  goalMilestones: true,
  quietHoursEnabled: false,
  quietHoursStart: 22,
  quietHoursEnd: 8,
}

/**
 * Check if notifications are supported on the current platform
 */
export function isNotificationSupported(): boolean {
  return Capacitor.isNativePlatform()
}

/**
 * Check if the app has notification permissions
 */
export async function checkNotificationPermissions(): Promise<boolean> {
  if (!isNotificationSupported()) {
    return false
  }

  try {
    const result = await LocalNotifications.checkPermissions()
    return result.display === "granted"
  } catch {
    return false
  }
}

/**
 * Request notification permissions from the user
 */
export async function requestNotificationPermissions(): Promise<boolean> {
  if (!isNotificationSupported()) {
    return false
  }

  try {
    const result = await LocalNotifications.requestPermissions()
    return result.display === "granted"
  } catch {
    return false
  }
}

/**
 * Create notification channels for Android
 */
export async function createNotificationChannels(): Promise<void> {
  if (!isNotificationSupported()) return

  try {
    await LocalNotifications.createChannel({
      id: NOTIFICATION_CHANNELS.BILL_REMINDERS,
      name: nt("channel_bills_name"),
      description: nt("channel_bills_desc"),
      importance: 4, // HIGH
      visibility: 0, // PUBLIC
      vibration: true,
    })

    await LocalNotifications.createChannel({
      id: NOTIFICATION_CHANNELS.BUDGET_ALERTS,
      name: nt("channel_budget_name"),
      description: nt("channel_budget_desc"),
      importance: 4,
      visibility: 0,
      vibration: true,
    })

    await LocalNotifications.createChannel({
      id: NOTIFICATION_CHANNELS.GOAL_MILESTONES,
      name: nt("channel_goals_name"),
      description: nt("channel_goals_desc"),
      importance: 3, // DEFAULT
      visibility: 0,
      vibration: true,
    })
  } catch (error) {
    logger.error("Failed to create notification channels:", error)
  }
}

/**
 * True when `hour` falls inside the user's quiet-hours window. The window may
 * wrap midnight (22 → 8) or not (9 → 17); start === end means no window.
 * Single implementation — this check was previously copy-pasted three times
 * with only the wrapping case handled, which silenced ALL notifications 24/7
 * for any non-wrapping window.
 */
function isInQuietHours(hour: number, preferences: NotificationPreferences): boolean {
  if (!preferences.quietHoursEnabled) return false
  const { quietHoursStart: start, quietHoursEnd: end } = preferences
  if (start === end) return false
  return start < end ? hour >= start && hour < end : hour >= start || hour < end
}

/**
 * djb2-style string hash folded to 0–99 999. Used for budget/goal notification
 * ids: char-code *summing* (the old scheme) collides for trivially different
 * strings ("Mat"/"Tam"), which made one category's alert replace another's.
 */
function hashString(value: string): number {
  let hash = 5381
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 33 + value.charCodeAt(i)) | 0
  }
  return Math.abs(hash) % 100_000
}

/**
 * Generate a unique notification ID for a bill reminder. Template hash and
 * due date get disjoint digit ranges — summing them (the previous scheme) let
 * "template A due day N+1" collide with "template B due day N", silently
 * replacing one reminder with the other.
 */
function generateBillNotificationId(templateId: string, dueDate: Date): number {
  const hash = templateId.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0)
  const dateNum = Math.floor(dueDate.getTime() / 86400000) // Days since epoch
  return NOTIFICATION_ID_PREFIX.BILL * 1_000_000 + (hash % 1000) * 100_000 + (dateNum % 100_000)
}

/**
 * Schedule bill reminder notifications for upcoming recurring expenses
 */
export async function scheduleBillReminders(preferences: NotificationPreferences): Promise<number> {
  if (!isNotificationSupported() || !preferences.enabled || !preferences.billReminders) {
    return 0
  }

  const hasPermission = await checkNotificationPermissions()
  if (!hasPermission) {
    return 0
  }

  try {
    // Cancel existing bill reminders first
    await cancelBillReminders()

    // Get upcoming recurring expenses for the next 30 days
    const upcomingExpenses = getUpcomingRecurringExpenses(30)
    const notifications: LocalNotificationSchema[] = []
    const now = new Date()

    for (const { template, dueDate } of upcomingExpenses) {
      // Calculate reminder date (X days before due date)
      const reminderDate = new Date(dueDate)
      reminderDate.setDate(reminderDate.getDate() - preferences.billReminderDays)

      // Set notification time to 9 AM
      reminderDate.setHours(9, 0, 0, 0)

      // Skip if reminder date is in the past
      if (reminderDate <= now) {
        // If due date is still in the future, schedule for now
        if (dueDate > now) {
          reminderDate.setTime(now.getTime() + 60000) // 1 minute from now
        } else {
          continue
        }
      }

      // Quiet hours: push the reminder to the end of the window. Only a
      // window that wraps midnight needs the day bump (22:xx → tomorrow 08:00).
      if (isInQuietHours(reminderDate.getHours(), preferences)) {
        const wraps = preferences.quietHoursStart > preferences.quietHoursEnd
        if (wraps && reminderDate.getHours() >= preferences.quietHoursStart) {
          reminderDate.setDate(reminderDate.getDate() + 1)
        }
        reminderDate.setHours(preferences.quietHoursEnd, 0, 0, 0)
      }

      const notificationId = generateBillNotificationId(template.id, dueDate)
      const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / 86400000)
      const dueText =
        daysUntilDue === 0
          ? nt("due_today")
          : daysUntilDue === 1
            ? nt("due_tomorrow")
            : nt("due_in_days", { days: daysUntilDue })

      notifications.push({
        id: notificationId,
        title: nt("bill_title"),
        body: nt("bill_body", {
          description: template.description,
          amount: formatMoney(template.amount),
          due: dueText,
        }),
        schedule: { at: reminderDate },
        channelId: NOTIFICATION_CHANNELS.BILL_REMINDERS,
        extra: {
          type: "bill_reminder",
          templateId: template.id,
          dueDate: dueDate.toISOString(),
        },
      })
    }

    if (notifications.length > 0) {
      await LocalNotifications.schedule({ notifications })
    }

    return notifications.length
  } catch (error) {
    logger.error("Failed to schedule bill reminders:", error)
    return 0
  }
}

/**
 * Cancel all scheduled bill reminder notifications
 */
export async function cancelBillReminders(): Promise<void> {
  if (!isNotificationSupported()) return

  try {
    const pending = await LocalNotifications.getPending()
    const billNotifications = pending.notifications.filter((n) => n.extra?.type === "bill_reminder")

    if (billNotifications.length > 0) {
      await LocalNotifications.cancel({ notifications: billNotifications })
    }
  } catch (error) {
    logger.error("Failed to cancel bill reminders:", error)
  }
}

// One alert per category, level, and calendar month. Without this memory the
// alert re-fires on EVERY expense added while over the threshold. Two levels
// ("warn" at the threshold, "over" at 100%) so crossing into overspend still
// notifies once more after the earlier warning.
const BUDGET_ALERTED_KEY = "budgetAlertsSent"

function wasBudgetAlertSent(categoryId: string, level: "warn" | "over", month: string): boolean {
  try {
    const map = JSON.parse(localStorage.getItem(BUDGET_ALERTED_KEY) ?? "{}") as Record<string, string>
    return map[`${categoryId}:${level}`] === month
  } catch {
    return false
  }
}

function markBudgetAlertSent(categoryId: string, level: "warn" | "over", month: string): void {
  try {
    const map = JSON.parse(localStorage.getItem(BUDGET_ALERTED_KEY) ?? "{}") as Record<string, string>
    // Keep only current-month entries so the map never grows unbounded.
    const pruned = Object.fromEntries(Object.entries(map).filter(([, m]) => m === month))
    pruned[`${categoryId}:${level}`] = month
    localStorage.setItem(BUDGET_ALERTED_KEY, JSON.stringify(pruned))
  } catch {
    // Storage unavailable — worst case the alert repeats, as before.
  }
}

/**
 * Send immediate budget alert notification
 */
export async function sendBudgetAlert(
  category: FirestoreCategory,
  spent: number,
  budget: number,
  preferences: NotificationPreferences,
): Promise<void> {
  if (!isNotificationSupported() || !preferences.enabled || !preferences.budgetAlerts) {
    return
  }

  const hasPermission = await checkNotificationPermissions()
  if (!hasPermission) return

  const percentage = (spent / budget) * 100
  if (percentage < preferences.budgetAlertThreshold) return

  if (isInQuietHours(new Date().getHours(), preferences)) {
    return // Don't send during quiet hours
  }

  try {
    const isOverBudget = percentage >= 100
    const level = isOverBudget ? "over" : "warn"
    const month = toLocalDateString().slice(0, 7)
    if (wasBudgetAlertSent(category.id, level, month)) return
    // Hash the stable category id (not the display name) so renames keep the
    // same notification slot, and use a spread hash — the old char-code sum
    // collided easily, letting one category's alert overwrite another's.
    const notificationId = NOTIFICATION_ID_PREFIX.BUDGET * 100_000 + hashString(category.id)

    await LocalNotifications.schedule({
      notifications: [
        {
          id: notificationId,
          title: isOverBudget ? nt("budget_exceeded_title") : nt("budget_warning_title"),
          body: isOverBudget
            ? nt("budget_exceeded_body", { category: category.name, amount: formatMoney(spent - budget) })
            : nt("budget_warning_body", { category: category.name, percent: percentage.toFixed(0) }),
          channelId: NOTIFICATION_CHANNELS.BUDGET_ALERTS,
          extra: {
            type: "budget_alert",
            categoryId: category.id,
            percentage,
          },
        },
      ],
    })
    markBudgetAlertSent(category.id, level, month)
  } catch (error) {
    logger.error("Failed to send budget alert:", error)
  }
}

/**
 * Send savings goal milestone notification
 */
export async function sendGoalMilestoneNotification(
  goalName: string,
  currentAmount: number,
  targetAmount: number,
  preferences: NotificationPreferences,
): Promise<void> {
  if (!isNotificationSupported() || !preferences.enabled || !preferences.goalMilestones) {
    return
  }

  const hasPermission = await checkNotificationPermissions()
  if (!hasPermission) return

  if (isInQuietHours(new Date().getHours(), preferences)) {
    return
  }

  const percentage = (currentAmount / targetAmount) * 100
  const milestones = [25, 50, 75, 100]
  const milestone = milestones.find((m) => percentage >= m && percentage < m + 5)

  if (!milestone) return

  try {
    const notificationId = NOTIFICATION_ID_PREFIX.GOAL * 100_000 + hashString(goalName)

    const isComplete = milestone >= 100

    await LocalNotifications.schedule({
      notifications: [
        {
          id: notificationId,
          title: isComplete ? nt("goal_achieved_title") : nt("goal_milestone_title"),
          body: isComplete
            ? nt("goal_achieved_body", { goal: goalName, target: formatMoney(targetAmount) })
            : nt("goal_milestone_body", {
                goal: goalName,
                milestone,
                current: formatMoney(currentAmount),
                target: formatMoney(targetAmount),
              }),
          channelId: NOTIFICATION_CHANNELS.GOAL_MILESTONES,
          extra: {
            type: "goal_milestone",
            goalName,
            milestone,
          },
        },
      ],
    })
  } catch (error) {
    logger.error("Failed to send goal milestone notification:", error)
  }
}

/**
 * Cancel all scheduled notifications
 */
export async function cancelAllNotifications(): Promise<void> {
  if (!isNotificationSupported()) return

  try {
    const pending = await LocalNotifications.getPending()
    if (pending.notifications.length > 0) {
      await LocalNotifications.cancel({ notifications: pending.notifications })
    }
  } catch (error) {
    logger.error("Failed to cancel all notifications:", error)
  }
}

/**
 * Get all pending notifications (for debugging/testing)
 */
export async function getPendingNotifications(): Promise<LocalNotificationSchema[]> {
  if (!isNotificationSupported()) return []

  try {
    const pending = await LocalNotifications.getPending()
    return pending.notifications
  } catch {
    return []
  }
}

/**
 * Initialize notification service - request permissions and create channels
 */
export async function initializeNotifications(): Promise<boolean> {
  if (!isNotificationSupported()) {
    logger.debug("Notifications not supported on this platform")
    return false
  }

  try {
    // Create channels first
    await createNotificationChannels()

    // Request permissions
    const granted = await requestNotificationPermissions()

    if (granted) {
      // Add listener for notification actions
      await LocalNotifications.addListener("localNotificationActionPerformed", (notification) => {
        logger.debug("Notification action performed", { notification: notification as unknown })
        // Handle notification tap - could navigate to relevant screen
      })
    }

    return granted
  } catch (error) {
    logger.error("Failed to initialize notifications:", error)
    return false
  }
}

/**
 * Refresh all scheduled notifications based on current data and preferences
 */
export async function refreshNotifications(preferences: NotificationPreferences): Promise<void> {
  if (!preferences.enabled) {
    await cancelAllNotifications()
    return
  }

  // Reschedule bill reminders
  await scheduleBillReminders(preferences)
}
